import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PipelineTracker } from '../../components/ui/PipelineTracker';
import { getTaxCase, transitionTaxCaseStatus, updateCurrency, updateEstimatedRefund } from '../../services/cases';
import { getDocumentsByTaxCase, presignUpload, uploadFileToPresignedUrl, downloadDocument, deleteDocument, viewDocument } from '../../services/documents';
import { getChecklist, updateChecklist, markItemCompleted } from '../../services/checklist';
import type { ChecklistItemRequest } from '../../types/checklist';
import { createEnvelope, sendEnvelope, getEnvelopesByTaxCase } from '../../services/envelopes';
import { createPayment, getPaymentsByTaxCase, markPaymentPaid } from '../../services/payments';
import { getUserById } from '../../services/users';
import { ApiClientError } from '../../services/api';
import type { Currency, TaxCaseStatus } from '../../types/taxCase';
import { formatCurrency, formatDate, formatDateTime } from '../../lib/format';

// Define allowed transitions for each status
const ALLOWED_TRANSITIONS: Record<TaxCaseStatus, TaxCaseStatus[]> = {
  'ONBOARDING': ['DOC_REQUEST'],
  'DOC_REQUEST': ['UNDER_REVIEW', 'ONBOARDING'],
  'UNDER_REVIEW': ['CALCULATION', 'DOC_REQUEST'],
  'CALCULATION': ['PENDING_SIGNATURE', 'UNDER_REVIEW'],
  'PENDING_SIGNATURE': ['AWAITING_PAYMENT', 'CALCULATION'],
  'AWAITING_PAYMENT': ['COMPLETED', 'PENDING_SIGNATURE'],
  'COMPLETED': []
};

export function CaseDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [paymentModal, setPaymentModal] = useState(false);
  const [amount, setAmount] = useState('250.00');
  const [due, setDue] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [checklistModal, setChecklistModal] = useState(false);
  const [checklistItems, setChecklistItems] = useState<ChecklistItemRequest[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [viewDocumentUrl, setViewDocumentUrl] = useState<string | null>(null);
  const [viewDocumentName, setViewDocumentName] = useState<string>('');
  const [refundModal, setRefundModal] = useState(false);
  const [estimatedRefund, setEstimatedRefund] = useState('');
  const [refundNote, setRefundNote] = useState('');
  const [currencyModal, setCurrencyModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('CAD');

  const caseQuery = useQuery({
    queryKey: ['tax-case', id],
    queryFn: () => getTaxCase(id!),
    enabled: Boolean(id),
  });

  const clientQuery = useQuery({
    queryKey: ['client', caseQuery.data?.clientId],
    queryFn: () => getUserById(caseQuery.data!.clientId),
    enabled: Boolean(caseQuery.data?.clientId),
  });

  const docsQuery = useQuery({
    queryKey: ['documents', id],
    queryFn: () => getDocumentsByTaxCase(id!),
    enabled: Boolean(id),
  });

  const paymentsQuery = useQuery({
    queryKey: ['payments', id],
    queryFn: () => getPaymentsByTaxCase(id!),
    enabled: Boolean(id),
  });

  const envelopesQuery = useQuery({
    queryKey: ['envelopes', id],
    queryFn: () => getEnvelopesByTaxCase(id!),
    enabled: Boolean(id),
  });

  const checklistQuery = useQuery({
    queryKey: ['checklist', id],
    queryFn: () => getChecklist(id!),
    enabled: Boolean(id),
  });

  const transitionMutation = useMutation({
    mutationFn: (targetStatus: TaxCaseStatus) =>
      transitionTaxCaseStatus(id!, { targetStatus }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['tax-case', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Unable to update status.';
      setError(message);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file || !id) throw new Error('Missing file or case ID');
      
      const presigned = await presignUpload({
        fileName: file.name,
        contentType: file.type,
        taxCaseId: id,
        sizeBytes: file.size,
      });
      
      await uploadFileToPresignedUrl(presigned.uploadUrl, file, file.type);
      return presigned;
    },
    onSuccess: () => {
      setFile(null);
      void qc.invalidateQueries({ queryKey: ['documents', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Upload failed.';
      setError(message);
    },
  });

  const envelopeMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const envelope = await createEnvelope({
        documentId,
        signerId: caseQuery.data!.clientId,
      });
      await sendEnvelope(envelope.id);
      return envelope;
    },
    onSuccess: () => {
      void qc.invalidateQueries();
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Failed to send envelope.';
      setError(message);
    },
  });

  const paymentMutation = useMutation({
    mutationFn: () =>
      createPayment({
        taxCaseId: id!,
        amount: Number(amount),
        dueDate: due,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      setPaymentModal(false);
      setFieldErrors({});
      void qc.invalidateQueries({ queryKey: ['payments', id] });
    },
    onError: (err) => {
      if (err instanceof ApiClientError) {
        if (err.fieldErrors && err.fieldErrors.length > 0) {
          const errors: Record<string, string> = {};
          err.fieldErrors.forEach(fe => {
            errors[fe.field] = fe.message;
          });
          setFieldErrors(errors);
          setError('Please fix the validation errors below');
        } else {
          setError(err.message);
        }
      } else {
        setError('Unable to create payment.');
      }
    },
  });

  const downloadMutation = useMutation({
    mutationFn: downloadDocument,
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Download failed.';
      setError(message);
    },
  });

  const viewMutation = useMutation({
    mutationFn: async (doc: { id: string; name: string }) => {
      const url = await viewDocument(doc.id);
      setViewDocumentUrl(url);
      setViewDocumentName(doc.name);
      return url;
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Failed to view document.';
      setError(message);
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: markPaymentPaid,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payments', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Failed to mark payment as paid.';
      setError(message);
    },
  });

  const updateRefundMutation = useMutation({
    mutationFn: () => updateEstimatedRefund(id!, {
      estimatedRefund: Number(estimatedRefund),
      note: refundNote || undefined,
    }),
    onSuccess: () => {
      setRefundModal(false);
      setFieldErrors({});
      void qc.invalidateQueries({ queryKey: ['tax-case', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Failed to update estimated refund.';
      setError(message);
    },
  });

  const updateCurrencyMutation = useMutation({
    mutationFn: () => updateCurrency(id!, { currency: selectedCurrency }),
    onSuccess: () => {
      setCurrencyModal(false);
      setFieldErrors({});
      void qc.invalidateQueries({ queryKey: ['tax-case', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Failed to update currency.';
      setError(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      setDeleteConfirm(null);
      void qc.invalidateQueries({ queryKey: ['documents', id] });
      void qc.invalidateQueries({ queryKey: ['checklist', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Delete failed.';
      setError(message);
    },
  });

  const checklistMutation = useMutation({
    mutationFn: () => updateChecklist(id!, checklistItems),
    onSuccess: () => {
      setChecklistModal(false);
      void qc.invalidateQueries({ queryKey: ['checklist', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Failed to update checklist.';
      setError(message);
    },
  });

  const checklistUploadMutation = useMutation({
    mutationFn: async ({ itemId, file }: { itemId: string; file: File }) => {
      const presigned = await presignUpload({
        taxCaseId: id!,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });
      await uploadFileToPresignedUrl(presigned.uploadUrl, file, file.type);
      return markItemCompleted(itemId, presigned.documentId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checklist', id] });
      void qc.invalidateQueries({ queryKey: ['documents', id] });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError ? err.message : 'Failed to upload document.';
      setError(message);
    },
  });


  if (!id) {
    return <div className="text-error">Missing case ID</div>;
  }

  if (caseQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-on-surface/60">
          <span className="material-symbols-outlined animate-spin">refresh</span>
          Loading case details...
        </div>
      </div>
    );
  }

  if (caseQuery.error || !caseQuery.data) {
    return <div className="text-error">Failed to load case details</div>;
  }

  const taxCase = caseQuery.data;
  const client = clientQuery.data;
  const allowedTransitions = ALLOWED_TRANSITIONS[taxCase.status as TaxCaseStatus] || [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link
              to="/admin/clients"
              className="text-on-surface/60 hover:text-primary transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </Link>
            <h2 className="text-3xl font-extrabold tracking-tight text-primary">
              Tax Case {taxCase.taxYear}
            </h2>
          </div>
          {client && (
            <p className="text-on-surface/60 font-medium">
              {client.firstName} {client.lastName} • {client.email}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`chip ${
            taxCase.status === 'COMPLETED' ? 'chip-primary' : 
            taxCase.status === 'PENDING_SIGNATURE' ? 'chip-error' : 'chip-tertiary'
          }`}>
            {taxCase.status.replace('_', ' ')}
          </span>
          <span className="text-on-surface/60 text-sm">
            Updated {formatDate(taxCase.updatedAt)}
          </span>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="bg-error-container text-on-error-container p-4 rounded-xl flex items-center gap-3">
          <span className="material-symbols-outlined">error</span>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto p-1 hover:bg-error/10 rounded"
          >
            <span className="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
      )}

      {/* Pipeline Tracker */}
      <PipelineTracker
        currentStatus={taxCase.status as TaxCaseStatus}
        allowedTransitions={allowedTransitions}
        onStatusChange={(status: TaxCaseStatus) => transitionMutation.mutate(status)}
        isLoading={transitionMutation.isPending}
      />

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Documents Section */}
        <section className="lg:col-span-2 space-y-6">
          {/* Document Checklist */}
          <div className="bg-surface-container-low rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-primary">Document Checklist</h3>
                <p className="text-on-surface/60 text-sm mt-1">
                  Required and optional documents for this case
                </p>
              </div>
              <button
                onClick={() => {
                  setChecklistItems(
                    checklistQuery.data?.map(item => ({
                      name: item.name,
                      description: item.description,
                      required: item.required,
                      displayOrder: item.displayOrder,
                    })) || []
                  );
                  setChecklistModal(true);
                }}
                className="text-primary hover:bg-primary/10 p-2 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined">edit</span>
              </button>
            </div>
            <div className="space-y-2">
              {checklistQuery.data?.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    item.completed ? 'bg-primary-container/20' : 'bg-surface-container'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <span className={`material-symbols-outlined ${
                      item.completed ? 'text-primary' : 'text-on-surface/40'
                    }`}>
                      {item.completed ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                    <div className="flex-1">
                      <p className={`font-semibold ${
                        item.completed ? 'text-primary' : 'text-on-surface'
                      }`}>
                        {item.name}
                        {item.required && <span className="text-error ml-1">*</span>}
                      </p>
                      {item.description && (
                        <p className="text-sm text-on-surface/60">{item.description}</p>
                      )}
                      {item.completed && item.fulfilledByDocumentName && (
                        <p className="text-sm text-primary mt-1">
                          ✓ {item.fulfilledByDocumentName}
                        </p>
                      )}
                    </div>
                  </div>
                  {!item.completed && (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            checklistUploadMutation.mutate({ itemId: item.id, file });
                          }
                        }}
                        disabled={checklistUploadMutation.isPending}
                      />
                      <button
                        type="button"
                        className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                        disabled={checklistUploadMutation.isPending}
                        onClick={(e) => {
                          e.preventDefault();
                          (e.currentTarget.previousElementSibling as HTMLInputElement)?.click();
                        }}
                      >
                        <span className="material-symbols-outlined text-sm">upload_file</span>
                        Upload
                      </button>
                    </label>
                  )}
                </div>
              ))}
              {(!checklistQuery.data || checklistQuery.data.length === 0) && (
                <div className="text-center py-6 text-on-surface/60">
                  <span className="material-symbols-outlined text-3xl mb-2 block">checklist</span>
                  <p className="text-sm">No checklist items yet</p>
                  <button
                    onClick={() => {
                      setChecklistItems([]);
                      setChecklistModal(true);
                    }}
                    className="mt-3 text-primary font-semibold hover:underline"
                  >
                    Create checklist
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-surface-container-low rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-primary">Documents</h3>
                <p className="text-on-surface/60 text-sm mt-1">
                  Upload and manage tax documents
                </p>
              </div>
              <span className="chip chip-tertiary">
                {docsQuery.data?.content.length || 0} files
              </span>
            </div>

            {/* Upload Form */}
            <form
              className="mb-6 p-4 bg-surface-container rounded-xl border-2 border-dashed border-outline-variant"
              onSubmit={(e) => {
                e.preventDefault();
                uploadMutation.mutate();
              }}
            >
              <div className="text-center">
                <span className="material-symbols-outlined text-4xl text-on-surface/40 mb-3 block">
                  cloud_upload
                </span>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="mb-4 block w-full text-sm text-on-surface/60 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white file:font-semibold hover:file:opacity-90"
                />
                <button
                  type="submit"
                  disabled={!file || uploadMutation.isPending}
                  className="bg-primary text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                >
                  {uploadMutation.isPending ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </form>

            {/* Documents List */}
            <div className="space-y-3">
              {docsQuery.data?.content.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 bg-surface-container rounded-xl"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 bg-primary-container rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-on-primary-container">
                        description
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-on-surface truncate">{doc.name}</p>
                      <p className="text-sm text-on-surface/60">
                        {doc.category} • {formatDateTime(doc.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => viewMutation.mutate({ id: doc.id, name: doc.name })}
                      disabled={viewMutation.isPending}
                      className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                      title="View"
                    >
                      <span className="material-symbols-outlined">visibility</span>
                    </button>
                    <button
                      onClick={() => downloadMutation.mutate(doc.id)}
                      disabled={downloadMutation.isPending}
                      className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                      title="Download"
                    >
                      <span className="material-symbols-outlined">download</span>
                    </button>
                    <button
                      onClick={() => envelopeMutation.mutate(doc.id)}
                      disabled={envelopeMutation.isPending}
                      className="bg-secondary text-white px-4 py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      Send for Signature
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(doc.id)}
                      className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              ))}
              {docsQuery.data?.content.length === 0 && (
                <div className="text-center py-8 text-on-surface/60">
                  <span className="material-symbols-outlined text-4xl mb-2 block">folder_open</span>
                  <p>No documents uploaded yet</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Sidebar */}
        <aside className="space-y-6">
          {/* Estimated Refund */}
          <div className="bg-surface-container-low rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Estimated Refund</h3>
              <button
                onClick={() => {
                  setEstimatedRefund(taxCase.estimatedRefund?.toString() || '');
                  setRefundNote(taxCase.estimatedRefundNote || '');
                  setRefundModal(true);
                }}
                className="text-primary hover:bg-primary/10 p-2 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined">edit</span>
              </button>
            </div>
            {taxCase.estimatedRefund != null ? (
              <div className="space-y-2">
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(taxCase.estimatedRefund, taxCase.currency)}
                </p>
                {taxCase.estimatedRefundNote && (
                  <p className="text-sm text-on-surface/70">
                    {taxCase.estimatedRefundNote}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-on-surface/60">
                No estimate set yet
              </p>
            )}
          </div>

          {/* Case Info */}
          <div className="bg-surface-container-low rounded-2xl p-6">
            <h3 className="text-lg font-bold text-primary mb-4">Case Information</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-on-surface/60">Tax Year</p>
                <p className="font-semibold text-on-surface">{taxCase.taxYear}</p>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-on-surface/60">Currency</p>
                  <button
                    onClick={() => {
                      setSelectedCurrency(taxCase.currency);
                      setCurrencyModal(true);
                    }}
                    className="text-primary hover:bg-primary/10 p-1 rounded transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">edit</span>
                  </button>
                </div>
                <p className="font-semibold text-on-surface">{taxCase.currency}</p>
              </div>
              <div>
                <p className="text-sm text-on-surface/60">Created</p>
                <p className="font-semibold text-on-surface">{formatDate(taxCase.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-on-surface/60">Last Updated</p>
                <p className="font-semibold text-on-surface">{formatDate(taxCase.updatedAt)}</p>
              </div>
              {taxCase.notes && (
                <div>
                  <p className="text-sm text-on-surface/60">Notes</p>
                  <p className="text-on-surface">{taxCase.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Signature Requests */}
          <div className="bg-surface-container-low rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Signatures</h3>
            </div>
            <div className="space-y-3">
              {envelopesQuery.data?.map((envelope) => (
                <div
                  key={envelope.id}
                  className="p-3 bg-surface-container rounded-lg space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-on-surface text-sm truncate">
                        {envelope.documentName}
                      </p>
                      <p className="text-xs text-on-surface/60">
                        {envelope.signerName}
                      </p>
                    </div>
                    <span className={`chip text-xs ${
                      envelope.status === 'SIGNED' ? 'chip-primary' : 
                      envelope.status === 'VIEWED' ? 'chip-tertiary' : 
                      envelope.status === 'SENT' ? 'chip-error' : 'chip-tertiary'
                    }`}>
                      {envelope.status}
                    </span>
                  </div>
                  {envelope.signedAt && (
                    <p className="text-xs text-primary">
                      ✓ Signed {formatDate(envelope.signedAt)}
                    </p>
                  )}
                </div>
              ))}
              {envelopesQuery.data?.length === 0 && (
                <div className="text-center py-4 text-on-surface/60">
                  <p className="text-sm">No signature requests</p>
                </div>
              )}
            </div>
          </div>

          {/* Payments */}
          <div className="bg-surface-container-low rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Payments</h3>
              <button
                onClick={() => setPaymentModal(true)}
                className="text-primary hover:bg-primary/10 p-2 rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined">add</span>
              </button>
            </div>
            <div className="space-y-3">
              {paymentsQuery.data?.map((payment) => (
                <div
                  key={payment.id}
                  className="p-3 bg-surface-container rounded-lg space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-on-surface">
                        {formatCurrency(payment.amount, taxCase.currency)}
                      </p>
                      <p className="text-sm text-on-surface/60">
                        Due {formatDate(payment.dueDate)}
                      </p>
                    </div>
                    <span className={`chip ${
                      payment.status === 'PAID' ? 'chip-primary' : 
                      payment.status === 'OVERDUE' ? 'chip-error' : 'chip-tertiary'
                    }`}>
                      {payment.status}
                    </span>
                  </div>
                  {payment.clientConfirmedAt && (
                    <div className="pt-2 border-t border-outline-variant">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="material-symbols-outlined text-primary text-base">
                          check_circle
                        </span>
                        <span className="text-primary font-semibold">
                          Client confirmed {formatDate(payment.clientConfirmedAt)}
                        </span>
                      </div>
                      {payment.clientConfirmationNote && (
                        <p className="text-sm text-on-surface/70 mt-1 ml-6">
                          {payment.clientConfirmationNote}
                        </p>
                      )}
                      {payment.proofOfPaymentDocumentId && (
                        <div className="flex items-center gap-2 mt-2 ml-6">
                          <span className="material-symbols-outlined text-sm text-secondary">
                            attach_file
                          </span>
                          <button
                            onClick={() => downloadMutation.mutate(payment.proofOfPaymentDocumentId!)}
                            className="text-sm text-secondary hover:underline"
                          >
                            {payment.proofOfPaymentDocumentName || 'Proof of payment'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {payment.status !== 'PAID' && payment.clientConfirmedAt && (
                    <button
                      onClick={() => markPaidMutation.mutate(payment.id)}
                      disabled={markPaidMutation.isPending}
                      className="w-full mt-2 bg-primary text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      Mark as Paid
                    </button>
                  )}
                </div>
              ))}
              {paymentsQuery.data?.length === 0 && (
                <div className="text-center py-4 text-on-surface/60">
                  <p className="text-sm">No payments created</p>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Payment Modal */}
      {paymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Create Payment</h3>
              <button
                onClick={() => setPaymentModal(false)}
                className="text-on-surface/60 hover:text-on-surface p-1"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                paymentMutation.mutate();
              }}
            >
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    if (fieldErrors.amount) {
                      const { amount: _, ...rest } = fieldErrors;
                      setFieldErrors(rest);
                    }
                  }}
                  className={`w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 ${
                    fieldErrors.amount ? 'ring-2 ring-error' : 'focus:ring-primary/20'
                  }`}
                  required
                />
                {fieldErrors.amount && (
                  <p className="mt-1 text-sm text-error">{fieldErrors.amount}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">
                  Due Date
                </label>
                <input
                  type="date"
                  value={due}
                  onChange={(e) => {
                    setDue(e.target.value);
                    if (fieldErrors.dueDate) {
                      const { dueDate: _, ...rest } = fieldErrors;
                      setFieldErrors(rest);
                    }
                  }}
                  className={`w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 ${
                    fieldErrors.dueDate ? 'ring-2 ring-error' : 'focus:ring-primary/20'
                  }`}
                  required
                />
                {fieldErrors.dueDate && (
                  <p className="mt-1 text-sm text-error">{fieldErrors.dueDate}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    if (fieldErrors.notes) {
                      const { notes: _, ...rest } = fieldErrors;
                      setFieldErrors(rest);
                    }
                  }}
                  className={`w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 ${
                    fieldErrors.notes ? 'ring-2 ring-error' : 'focus:ring-primary/20'
                  }`}
                  rows={3}
                />
                {fieldErrors.notes && (
                  <p className="mt-1 text-sm text-error">{fieldErrors.notes}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={paymentMutation.isPending}
                  className="flex-1 bg-primary text-white py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {paymentMutation.isPending ? 'Creating...' : 'Create Payment'}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentModal(false)}
                  className="px-4 py-2 text-on-surface/60 hover:text-on-surface transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Checklist Modal */}
      {checklistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Edit Document Checklist</h3>
              <button
                onClick={() => setChecklistModal(false)}
                className="text-on-surface/60 hover:text-on-surface p-1"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="space-y-4">
              {checklistItems.map((item, index) => (
                <div key={index} className="p-4 bg-surface-container rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-3">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => {
                          const updated = [...checklistItems];
                          updated[index].name = e.target.value;
                          setChecklistItems(updated);
                        }}
                        placeholder="Document name"
                        className="w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 focus:ring-primary/20"
                      />
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => {
                          const updated = [...checklistItems];
                          updated[index].description = e.target.value;
                          setChecklistItems(updated);
                        }}
                        placeholder="Description (optional)"
                        className="w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 focus:ring-primary/20"
                        rows={2}
                      />
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={item.required}
                          onChange={(e) => {
                            const updated = [...checklistItems];
                            updated[index].required = e.target.checked;
                            setChecklistItems(updated);
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-on-surface">Required</span>
                      </label>
                    </div>
                    <button
                      onClick={() => {
                        setChecklistItems(checklistItems.filter((_, i) => i !== index));
                      }}
                      className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  setChecklistItems([
                    ...checklistItems,
                    { name: '', description: '', required: true, displayOrder: checklistItems.length },
                  ]);
                }}
                className="w-full p-3 border-2 border-dashed border-outline-variant rounded-lg text-primary font-semibold hover:bg-primary/5 transition-colors"
              >
                + Add Item
              </button>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => checklistMutation.mutate()}
                  disabled={checklistMutation.isPending}
                  className="flex-1 bg-primary text-white py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {checklistMutation.isPending ? 'Saving...' : 'Save Checklist'}
                </button>
                <button
                  onClick={() => setChecklistModal(false)}
                  className="px-4 py-2 text-on-surface/60 hover:text-on-surface transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <span className="material-symbols-outlined text-3xl text-error">warning</span>
              <h3 className="text-lg font-bold text-error">Delete Document</h3>
            </div>
            <p className="text-on-surface mb-6">
              Are you sure you want to permanently delete this document? This action cannot be undone and will remove the file from S3 storage.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="flex-1 bg-error text-white py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Permanently'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-on-surface/60 hover:text-on-surface transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewDocumentUrl && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-outline-variant">
              <h3 className="text-lg font-bold text-primary truncate">{viewDocumentName}</h3>
              <button
                onClick={() => {
                  if (viewDocumentUrl) {
                    window.URL.revokeObjectURL(viewDocumentUrl);
                  }
                  setViewDocumentUrl(null);
                  setViewDocumentName('');
                }}
                className="text-on-surface/60 hover:text-on-surface p-2 rounded-lg hover:bg-surface-container transition-colors"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={viewDocumentUrl}
                className="w-full h-full"
                title={viewDocumentName}
              />
            </div>
          </div>
        </div>
      )}

      {/* Estimated Refund Modal */}
      {refundModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Set Estimated Refund</h3>
              <button
                onClick={() => setRefundModal(false)}
                className="text-on-surface/60 hover:text-on-surface p-1"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                updateRefundMutation.mutate();
              }}
            >
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">
                  Estimated Refund Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={estimatedRefund}
                  onChange={(e) => {
                    setEstimatedRefund(e.target.value);
                    if (fieldErrors.estimatedRefund) {
                      const { estimatedRefund: _, ...rest } = fieldErrors;
                      setFieldErrors(rest);
                    }
                  }}
                  className={`w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 ${
                    fieldErrors.estimatedRefund ? 'ring-2 ring-error' : 'focus:ring-primary/20'
                  }`}
                  placeholder="0.00"
                  required
                />
                {fieldErrors.estimatedRefund && (
                  <p className="mt-1 text-sm text-error">{fieldErrors.estimatedRefund}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">
                  Note (optional)
                </label>
                <textarea
                  value={refundNote}
                  onChange={(e) => {
                    setRefundNote(e.target.value);
                    if (fieldErrors.note) {
                      const { note: _, ...rest } = fieldErrors;
                      setFieldErrors(rest);
                    }
                  }}
                  className={`w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 ${
                    fieldErrors.note ? 'ring-2 ring-error' : 'focus:ring-primary/20'
                  }`}
                  placeholder="e.g., Based on preliminary calculations"
                  rows={3}
                />
                {fieldErrors.note && (
                  <p className="mt-1 text-sm text-error">{fieldErrors.note}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={updateRefundMutation.isPending}
                  className="flex-1 bg-primary text-white py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {updateRefundMutation.isPending ? 'Saving...' : 'Save Estimate'}
                </button>
                <button
                  type="button"
                  onClick={() => setRefundModal(false)}
                  className="px-4 py-2 text-on-surface/60 hover:text-on-surface transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Currency Modal */}
      {currencyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-primary">Change Currency</h3>
              <button
                onClick={() => setCurrencyModal(false)}
                className="text-on-surface/60 hover:text-on-surface p-1"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                updateCurrencyMutation.mutate();
              }}
            >
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">
                  Select Currency
                </label>
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value as Currency)}
                  className="w-full px-3 py-2 bg-surface-container-high rounded-lg border-0 focus:ring-2 focus:ring-primary/20"
                >
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={updateCurrencyMutation.isPending}
                  className="flex-1 bg-primary text-white py-2 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {updateCurrencyMutation.isPending ? 'Saving...' : 'Update Currency'}
                </button>
                <button
                  type="button"
                  onClick={() => setCurrencyModal(false)}
                  className="px-4 py-2 text-on-surface/60 hover:text-on-surface transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}